import { LogisticsProjectLookupComponent } from '../../../logistics-projects/logistics-project-lookup.component';
import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageSearchPanelComponent } from '../../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { VehicleType } from '../../vehicle/params/vehicle-type';
import { FleetTrailerType } from '../../trailer/models/trailer.model';
import { FleetTrailerService } from '../../trailer/trailer.service';
import { Mobilization, MobilizationDashboard, MobilizationStatus } from '../models/mobilization.model';
import { MobilizationService } from '../mobilization.service';

@Component({
    selector: 'app-mobilization-overview',
    standalone: true,
    templateUrl: './mobilization-overview.component.html',
    styleUrls: ['./mobilization-overview.component.css'],
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        FormsModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        DatePickerModule,
        IftaLabelModule,
        InputGroupAddonModule,
        InputGroupModule,
        InputTextModule,
        SelectModule,
        TableModule,
        TagModule,
        ToastModule,
        PageShellComponent,
        PageHeroComponent,
        PageSearchPanelComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ]
})
export class MobilizationOverviewComponent implements OnInit {
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    mobilizations: Mobilization[] = [];
    dashboard?: MobilizationDashboard;
    isLoading = false;
    keyword = '';
    selectedStatus: MobilizationStatus | '' = '';
    dateRange: Date[] | null = null;
    vehicleTypeId?: number;
    trailerTypeId?: number;
    logisticsProjectId?: number;
    vehicleTypes: VehicleType[] = [];
    trailerTypes: FleetTrailerType[] = [];

    totalRecords = 0;

    readonly statusOptions = [
        { label: 'Draft', value: 'DRAFT' },
        { label: 'Submitted', value: 'SUBMITTED' },
        { label: 'Approved', value: 'APPROVED' },
        { label: 'Rejected', value: 'REJECTED' },
        { label: 'Active', value: 'ACTIVE' },
        { label: 'Completed', value: 'COMPLETED' },
        { label: 'Cancelled', value: 'CANCELLED' }
    ];

    constructor(
        private mobilizationService: MobilizationService,
        private trailerService: FleetTrailerService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.breadcrumbItems = [{ label: 'Fleet', routerLink: '/fleet' }, { label: 'Mobilization' }];
        this.vehicleTypes = this.sessionStorageService.getVehicleTypes() ?? [];
        this.dateRange = this.currentMonthRange();
        this.loadTrailerTypes();
        this.loadMobilizations();
    }

    loadMobilizations(): void {
        const companyId = this.companyId();
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Company missing', detail: 'No owning company is available in session.' });
            return;
        }

        this.isLoading = true;
        forkJoin({
            dashboard: this.mobilizationService.getDashboard({
                owningCompanyId: companyId,
                dateFrom: this.dateRange?.[0],
                dateTo: this.dateRange?.[1],
                status: this.selectedStatus,
                vehicleTypeId: this.vehicleTypeId,
                trailerTypeId: this.trailerTypeId,
                logisticsProjectId: this.logisticsProjectId
            }),
            mobilizations: this.mobilizationService.searchMobilizations({
                owningCompanyId: companyId,
                page: 0,
                pageSize: 100,
                keyword: this.keyword,
                status: this.selectedStatus,
                startFrom: this.dateRange?.[0],
                startTo: this.dateRange?.[1],
                vehicleTypeId: this.vehicleTypeId,
                trailerTypeId: this.trailerTypeId,
                logisticsProjectId: this.logisticsProjectId
            })
        }).subscribe({
            next: response => {
                this.dashboard = response.dashboard.data;
                this.mobilizations = response.mobilizations.items ?? [];
                this.totalRecords = response.mobilizations.count ?? this.mobilizations.length;
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load mobilizations.' });
            }
        });
    }

    clearSearch(): void {
        this.keyword = '';
        this.selectedStatus = '';
        this.dateRange = this.currentMonthRange();
        this.vehicleTypeId = undefined;
        this.trailerTypeId = undefined;
        this.logisticsProjectId = undefined;
        this.loadMobilizations();
    }

    metrics(): PageMetric[] {
        const summary = this.dashboard?.summary;

        return [
            { label: 'Total Assignments', value: summary?.totalMobilizations ?? this.totalRecords, hint: 'Dashboard date range', tone: 'info' },
            { label: 'Active', value: summary?.activeMobilizations ?? this.countByStatus('ACTIVE'), hint: 'Live assignments', tone: 'success' },
            { label: 'Drivers', value: summary?.assignedDrivers ?? 0, hint: 'Assigned driver count', tone: 'default' },
            { label: 'Trucks', value: summary?.assignedVehicles ?? 0, hint: 'Assigned truck count', tone: 'default' },
            { label: 'Trailers', value: summary?.assignedTrailers ?? 0, hint: 'Assignments with trailers', tone: 'warn' },
            { label: 'Salary Total', value: this.salaryTotalLabel(), hint: 'Calculated salary total', tone: 'info' },
            { label: 'Missing Rules', value: summary?.missingSalaryRuleCount ?? 0, hint: 'Assignments without salary rules', tone: summary?.missingSalaryRuleCount ? 'danger' : 'success' }
        ];
    }

    activeMobilizations(): Mobilization[] {
        return this.mobilizations.filter(item => item.status === 'ACTIVE').slice(0, 8);
    }

    approvalQueue(): Mobilization[] {
        return this.mobilizations.filter(item => item.status === 'SUBMITTED' || item.status === 'REJECTED').slice(0, 8);
    }

    expiringSoon(): Mobilization[] {
        const today = new Date();
        const limit = new Date();
        limit.setDate(today.getDate() + 7);
        return this.mobilizations.filter(item => {
            const end = item.endDate ? new Date(item.endDate) : null;
            return item.status === 'ACTIVE' && !!end && end >= today && end <= limit;
        });
    }

    openMobilization(item: Mobilization): void {
        if (item.mobilizationId) {
            this.router.navigate(['/fleet/mobilization', item.mobilizationId]);
        }
    }

    projectLabel(item: Mobilization): string {
        if (item.projectSource === 'LOGISTICS_PROJECT') {
            return item.logisticsProjectId ? `Logistics Project #${item.logisticsProjectId}` : 'Logistics Project';
        }
        return [item.manualProjectCode, item.manualProjectName].filter(Boolean).join(' - ') || 'Manual project';
    }

    siteLabel(item: Mobilization): string {
        return item.manualSiteName || item.manualSiteAddress || 'Site not set';
    }

    trailerLabel(item: Mobilization): string {
        return item.trailerAssetNumber || item.trailerPlateNumber || 'No trailer';
    }

    salaryLabel(item: Mobilization): string {
        if (item.salaryCalculationStatus === 'NO_RULE') {
            return 'No rule';
        }
        if (item.salaryTotalAmount === null || item.salaryTotalAmount === undefined) {
            return '-';
        }
        return `${item.salaryTotalAmount} ${item.salaryCurrencyCode || 'SAR'}`;
    }

    dateLabel(value?: string | Date): string {
        return value ? formatDate(value, 'MMM d, y', 'en-US') : '-';
    }

    salaryTotalLabel(): string {
        const summary = this.dashboard?.summary;
        if (!summary?.salaryTotalAmount) {
            return `0 ${summary?.salaryCurrencyCode || 'SAR'}`;
        }
        return `${summary.salaryTotalAmount} ${summary.salaryCurrencyCode || 'SAR'}`;
    }

    amountLabel(value?: number): string {
        return `${value ?? 0} ${this.dashboard?.summary?.salaryCurrencyCode || 'SAR'}`;
    }

    statusSeverity(status?: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
        switch (status) {
            case 'ACTIVE':
            case 'COMPLETED':
                return 'success';
            case 'SUBMITTED':
            case 'APPROVED':
                return 'warning';
            case 'REJECTED':
            case 'CANCELLED':
                return 'danger';
            case 'DRAFT':
                return 'secondary';
            default:
                return 'info';
        }
    }

    activeFilterCount(): number {
        return [this.keyword, this.selectedStatus, this.dateRange?.[0], this.dateRange?.[1], this.vehicleTypeId, this.trailerTypeId, this.logisticsProjectId].filter(Boolean).length;
    }

    private countByStatus(status: MobilizationStatus): number {
        return this.mobilizations.filter(item => item.status === status).length;
    }

    private companyId(): number {
        return this.sessionStorageService.getCompany()?.id ?? 0;
    }

    private loadTrailerTypes(): void {
        const companyId = this.companyId();
        if (!companyId) {
            return;
        }
        this.trailerService.searchTrailerTypes({ owningCompanyId: companyId, page: 0, pageSize: 250, active: true })
            .subscribe({
                next: response => this.trailerTypes = response.items ?? [],
                error: () => this.messageService.add({ severity: 'warn', summary: 'Trailer types', detail: 'Could not load trailer type filters.' })
            });
    }

    private currentMonthRange(): Date[] {
        const now = new Date();
        return [new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 0)];
    }
}
